import { useCallback, useEffect, useState } from 'react';
import type { TRPC } from '../../contexts/TRPC';
import { isLeftResult, isRightResult } from '../../lib/trpcTypeGuards';
import { captureAndReportError } from '../../utils/errorReporting';
import type { SuperPublisherDetailData } from './types';

interface UseSuperPublisherDetailOptions {
  publisherUserJoinId?: string;
  trpc: TRPC;
  onError: (message: string) => void;
}

export function useSuperPublisherDetail({
  publisherUserJoinId,
  trpc,
  onError,
}: UseSuperPublisherDetailOptions) {
  const [data, setData] = useState<SuperPublisherDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!publisherUserJoinId) return;

    try {
      setIsLoading(true);
      const result = await trpc.client.getSuperPublisherDetail.query({
        publisherUserJoinId,
      });

      if (isRightResult(result)) {
        setData(result.right);
      } else if (isLeftResult(result)) {
        onError(result.left.message);
      }
    } catch (error) {
      captureAndReportError(error, {
        operation: 'Failed to load super publisher detail',
        component: 'useSuperPublisherDetail',
      });
      onError('Failed to load publisher details');
    } finally {
      setIsLoading(false);
    }
  }, [publisherUserJoinId, onError, trpc]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    data,
    isLoading,
    reload: load,
  };
}
